import React, { useState } from 'react';
import { Sparkle } from 'phosphor-react';
import { ProductCategory, CustomCategory } from '../../types/entities';
import Skeleton, { SkeletonArea } from '../ui/Skeleton';

interface SelectedCategory {
  id: string;
  name: string;
  isCustom: boolean;
}

interface CategoryPickerProps {
  categories?: ProductCategory[];
  customCategories?: CustomCategory[];
  selectedId: string;
  selectedIsCustom: boolean;
  onSelect: (cat: SelectedCategory) => void;
  /**
   * Sem isto, "ainda carregando" e "não há categoria" ficavam idênticos: uma
   * grade vazia com o botão "Nenhuma" sozinho.
   */
  carregando?: boolean;
}

export default function CategoryPicker({
  categories = [],
  customCategories = [],
  selectedId,
  selectedIsCustom,
  onSelect,
  carregando = false,
}: CategoryPickerProps) {
  const [search, setSearch] = useState('');

  const term = search.toLowerCase();
  const filteredSystem = categories.filter((c) => c.name.toLowerCase().includes(term));
  const filteredCustom = customCategories.filter((c) => c.name.toLowerCase().includes(term));

  const noneSelected = !selectedId;

  const isSelected = (id: string, isCustom: boolean) =>
    selectedId === id && !!selectedIsCustom === !!isCustom;

  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-medium text-nude-700 dark:text-gray-400 tracking-wide uppercase">
        Categoria
      </label>
      <input
        type="text"
        placeholder="Buscar categoria..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="w-full px-3 py-2 text-xs bg-nude-50 dark:bg-gray-700 border border-nude-200 dark:border-gray-600 rounded-xl text-gray-700 dark:text-gray-200 placeholder-nude-300 dark:placeholder-gray-500 focus:outline-none focus:border-blush-300 transition-colors"
        disabled={carregando}
      />

      {carregando ? (
        <SkeletonArea className="grid grid-cols-2 gap-1.5" label="Carregando categorias">
          {Array.from({ length: 6 }, (_, i) => (
            <Skeleton key={i} className="h-[52px] rounded-xl" />
          ))}
        </SkeletonArea>
      ) : (
      <div className="grid grid-cols-2 gap-1.5 max-h-44 overflow-y-auto pr-0.5">
        <button
          type="button"
          onClick={() => onSelect({ id: '', name: '', isCustom: false })}
          className={`
            text-left p-2.5 rounded-xl border-2 text-xs transition-colors active:scale-[0.97]
            ${noneSelected
              ? 'border-blush-300 bg-blush-50 dark:bg-blush-900/20'
              : 'border-transparent bg-nude-50 dark:bg-gray-700 hover:border-blush-100 dark:hover:bg-gray-600'
            }
          `}
        >
          <p className="font-semibold text-gray-700 dark:text-gray-200">Nenhuma</p>
        </button>

        {filteredSystem.map((cat) => (
          <button
            key={`sys-${cat.id}`}
            type="button"
            onClick={() => onSelect({ id: cat.id, name: cat.name, isCustom: false })}
            className={`
              text-left p-2.5 rounded-xl border-2 text-xs transition-colors active:scale-[0.97]
              ${isSelected(cat.id, false)
                ? 'border-blush-300 bg-blush-50 dark:bg-blush-900/20'
                : 'border-transparent bg-nude-50 dark:bg-gray-700 hover:border-blush-100 dark:hover:bg-gray-600'
              }
            `}
          >
            <p className="font-semibold text-gray-700 dark:text-gray-200 truncate">{cat.name}</p>
            {cat.recommendedActives && (
              <p className="text-nude-400 dark:text-gray-500 truncate mt-0.5">{cat.recommendedActives}</p>
            )}
          </button>
        ))}

        {filteredCustom.map((cat) => (
          <button
            key={`custom-${cat.id}`}
            type="button"
            onClick={() => onSelect({ id: cat.id, name: cat.name, isCustom: true })}
            className={`
              text-left p-2.5 rounded-xl border-2 text-xs transition-colors active:scale-[0.97]
              ${isSelected(cat.id, true)
                ? 'border-blush-300 bg-blush-50 dark:bg-blush-900/20'
                : 'border-transparent bg-nude-50 dark:bg-gray-700 hover:border-blush-100 dark:hover:bg-gray-600'
              }
            `}
          >
            <div className="flex items-center gap-1 mb-0.5">
              <p className="font-semibold text-gray-700 dark:text-gray-200 truncate">{cat.name}</p>
              <Sparkle className="w-2.5 h-2.5 text-blush-400 flex-shrink-0" />
            </div>
            {cat.recommendedActives && (
              <p className="text-nude-400 dark:text-gray-500 truncate">{cat.recommendedActives}</p>
            )}
          </button>
        ))}
      </div>
      )}
    </div>
  );
}
